import EmptyState from '@/components/EmptyState';
import { ApiError } from '@/api/errors';
import { useTableStates } from '@/features/table-ordering/hooks/useTableOrdering';
import { useT } from '@/i18n';
import type { WaitlistEntry } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  Stepper,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import {
  useAddToWaitlist,
  useCancelWaitlist,
  useMarkNoShow,
  useNotifyWhenReady,
  useSeatWaitlist,
  useWaitlist,
} from '../hooks/useWaitlist';

function toast(text: string) {
  f7.toast.show({ text, closeTimeout: 2500, position: 'bottom' });
}

function errText(e: unknown, fallback: string) {
  return e instanceof ApiError ? e.message : fallback;
}

/** Aging colour: past the quoted wait → red, within 5 min of it → orange. */
function agingColor(e: WaitlistEntry): string {
  if (e.waitingMinutes >= e.quotedWaitMinutes) return 'var(--f7-color-red)';
  if (e.waitingMinutes >= e.quotedWaitMinutes - 5) return 'var(--f7-color-orange)';
  return 'var(--f7-text-color)';
}

export default function WaitlistPage() {
  const t = useT();
  const wl = t.waitlist;
  const { data: entries, isLoading, error } = useWaitlist();
  const { data: tables } = useTableStates();
  const add = useAddToWaitlist();
  const notify = useNotifyWhenReady();
  const seat = useSeatWaitlist();
  const cancel = useCancelWaitlist();
  const noShow = useMarkNoShow();

  const [showForm, setShowForm] = useState(false);
  const [partySize, setPartySize] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const list = entries ?? [];
  const available = (tables ?? []).filter((t) => t.state === 'AVAILABLE');

  const resetForm = () => {
    setPartySize(2);
    setName('');
    setPhone('');
    setShowForm(false);
  };

  const doAdd = () => {
    if (!name.trim()) {
      toast(wl.validation.nameRequired);
      return;
    }
    add.mutate(
      {
        partySize,
        guestName: name.trim(),
        guestPhone: phone.trim() || undefined,
      },
      {
        onSuccess: (e) => {
          toast(wl.toasts.added(e.guestName, e.quotedWaitMinutes));
          resetForm();
        },
        onError: (e) => toast(errText(e, wl.toasts.could_not_add)),
      },
    );
  };

  const onNotify = (e: WaitlistEntry) => {
    notify.mutate(e.id, {
      onSuccess: () => toast(wl.toasts.marked_notified(e.guestName)),
      onError: (err) => toast(errText(err, wl.toasts.could_not_update)),
    });
  };

  const onSeat = (e: WaitlistEntry) => {
    if (available.length === 0) {
      toast(wl.toasts.no_free_tables);
      return;
    }
    f7.actions
      .create({
        buttons: [
          {
            text: wl.actions.seat.label(e.guestName, e.partySize),
            label: true,
          },
          ...available.map((table) => ({
            text: wl.actions.seat.tableOption(
              table.label,
              table.zoneLabel ? ` · ${table.zoneLabel}` : '',
              table.seats,
            ),
            onClick: () =>
              seat.mutate(
                { entryId: e.id, tableId: table.tableId },
                {
                  onSuccess: (r) => {
                    toast(wl.toasts.seated(e.guestName));
                    f7.views.current.router.navigate(`/tables/session/${r.tableSessionId}`);
                  },
                  onError: (err) => toast(errText(err, wl.toasts.could_not_seat)),
                },
              ),
          })),
          { text: t.cancel, color: 'red' },
        ],
      })
      .open();
  };

  const onCancel = (e: WaitlistEntry) => {
    f7.dialog.confirm(wl.actions.cancel.confirm(e.guestName), wl.actions.cancel.title, () => {
      cancel.mutate(e.id, {
        onSuccess: () => toast(wl.toasts.removed(e.guestName)),
        onError: (err) => toast(errText(err, wl.toasts.could_not_remove)),
      });
    });
  };

  const onNoShow = (e: WaitlistEntry) => {
    f7.dialog.confirm(wl.actions.noShow.confirm(e.guestName), wl.actions.noShow.title, () => {
      noShow.mutate(e.id, {
        onSuccess: () => toast(wl.toasts.marked_noshow(e.guestName)),
        onError: (err) => toast(errText(err, wl.toasts.could_not_update)),
      });
    });
  };

  return (
    <Page name="waitlist">
      <Navbar title={wl.title} backLink={t.nav.tables}>
        <NavRight>
          <F7Link iconF7={showForm ? 'xmark' : 'plus'} onClick={() => setShowForm((v) => !v)} />
        </NavRight>
      </Navbar>

      {showForm && (
        <>
          <BlockTitle>{wl.addForm.title}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem noChevron title={wl.addForm.partySize}>
              <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                person_2
              </i>
              <Stepper
                slot="after"
                value={partySize}
                min={1}
                max={30}
                onStepperChange={setPartySize}
                small
                raisedMd
              />
            </ListItem>
            <ListInput
              label={wl.addForm.guestName}
              type="text"
              placeholder={wl.addForm.guestNamePlaceholder}
              value={name}
              onInput={(e) => setName(e.target.value)}
            />
            <ListInput
              label={wl.addForm.phone}
              type="tel"
              placeholder={wl.addForm.phonePlaceholder}
              value={phone}
              onInput={(e) => setPhone(e.target.value)}
            />
          </List>
          <Block>
            <Button fill large onClick={doAdd} disabled={add.isPending}>
              {add.isPending ? wl.addForm.adding : wl.addForm.addButton}
            </Button>
          </Block>
        </>
      )}

      {isLoading && (
        <Block style={{ textAlign: 'center', paddingTop: 32 }}>
          <Preloader />
        </Block>
      )}
      {error && (
        <Block style={{ paddingTop: 16 }}>
          <EmptyState
            icon="exclamationmark_triangle"
            iconColor="#ff9500"
            title={wl.error.loading}
            description={(error as Error).message}
          />
        </Block>
      )}

      {entries && list.length === 0 && !showForm && (
        <Block style={{ paddingTop: 32 }}>
          <EmptyState icon="person_2" title={wl.empty.title} description={wl.empty.description} />
        </Block>
      )}

      {list.length > 0 && (
        <List strongIos insetIos dividersIos mediaList style={{ marginTop: 16 }}>
          {list.map((e) => (
            <ListItem key={e.id} title={e.guestName} subtitle={wl.partyOf(e.partySize)}>
              <div slot="after-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {e.status === 'NOTIFIED' && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--f7-color-green)',
                    }}
                  >
                    {wl.status.notified}
                  </span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: agingColor(e) }}>
                  {e.waitingMinutes}/{e.quotedWaitMinutes} min
                </span>
              </div>
              {e.guestPhone && (
                <div slot="text" style={{ color: 'var(--f7-list-item-text-text-color)' }}>
                  {e.guestPhone}
                </div>
              )}
              <div
                slot="footer"
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}
              >
                {e.status === 'WAITING' && (
                  <Button small onClick={() => onNotify(e)} disabled={notify.isPending}>
                    {wl.actions.notify}
                  </Button>
                )}
                <Button small fill onClick={() => onSeat(e)} disabled={seat.isPending}>
                  {wl.actions.seatButton}
                </Button>
                <Button small onClick={() => onNoShow(e)} disabled={noShow.isPending}>
                  {wl.actions.noshow}
                </Button>
                <Button small color="red" onClick={() => onCancel(e)} disabled={cancel.isPending}>
                  {wl.actions.remove}
                </Button>
              </div>
            </ListItem>
          ))}
        </List>
      )}
    </Page>
  );
}
